import { RawFile } from "@mapmycustomers/shared/types/File";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import CoachingFile from "@app/scene/dashboard/types/CoachingFile";
import { CoachingNoteAttachmentChip } from "@app/scene/dashboard/types/CoachingNote";

// CoachingFilePicker emits CoachingFile row ids; downstream callers need the
// underlying RawFile (platform) ids, so these resolvers bridge id → row →
// fileId against the loaded pool.

export const resolveCoachingFiles = (
  pool: CoachingFile[],
  ids: Array<CoachingFile["id"]>
): CoachingFile[] => {
  const fileById = new Map(pool.map((file) => [file.id, file]));
  return ids.map((id) => fileById.get(id)).filter(isDefined);
};

export const resolvePlatformFileIds = (
  pool: CoachingFile[],
  ids: Array<CoachingFile["id"]>
): Array<RawFile["id"]> => resolveCoachingFiles(pool, ids).map(({ fileId }) => fileId);

// Activity files whose coaching-pool entry was deleted. They can't be resolved
// through the pool but still belong on the activity — the server still serves
// them for preview/download. Returns the subset of existingActivityFiles whose
// platform file ID does NOT appear in any pool entry.
export const getOrphanedActivityFiles = (
  pool: CoachingFile[],
  existingActivityFiles: Array<Pick<RawFile, "id" | "name">>
): Array<Pick<RawFile, "id" | "name">> => {
  const poolPlatformIds = new Set(pool.map(({ fileId }) => fileId));
  return existingActivityFiles.filter(({ id }) => !poolPlatformIds.has(id));
};

// CoachingFile (the full pool-row shape) → CoachingNoteAttachmentChip (the BE's
// denormalized note-chip shape). Used on the optimistic note-compose / inline-
// edit paths so the temp note renders chips in the same shape the server will
// hydrate on the next list refetch. Drops `fileId`, `subjectUserId`, `createdAt`,
// `tagIds`, `tags` since the chip view doesn't need them.
export const toAttachmentChip = (file: CoachingFile): CoachingNoteAttachmentChip => ({
  coachingFileId: file.id,
  contentLength: file.contentLength,
  contentType: file.contentType,
  name: file.name,
  uploaderId: file.uploaderId,
  user: file.user,
});
